"use client";

import { useState } from "react";
import Switch from "@/components/ui/switch/Switch";
import { useAlertContext } from "@/providers/AlertProvider";
import { userMessage } from "@/api/errors";

/**
 * Row-level active/published toggle for a paginated admin table — every one of these entities
 * (HeroSlide, PromoTile, Campaign, DiscountRule, Category, Testimonial, Location, Blog) already
 * has a real full-replace PUT/update endpoint and an `active`/`published` boolean; this doesn't
 * add new backend endpoints, it just flips that one field via the same mutation the edit modal
 * already uses, sending the full current row body (mutating a copy) since these are PUT, not
 * PATCH, endpoints.
 */
export function InlineToggle({
  checked,
  onToggle,
  label,
}: {
  checked: boolean;
  onToggle: (next: boolean) => Promise<void>;
  label?: string;
}) {
  const { error } = useAlertContext();
  const [pending, setPending] = useState(false);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);

  const value = optimistic ?? checked;

  async function handleChange(next: boolean) {
    setOptimistic(next);
    setPending(true);
    try {
      await onToggle(next);
    } catch (err) {
      setOptimistic(null); // rollback — the underlying query's `checked` prop is still the old value
      error("Update failed", userMessage(err));
    } finally {
      setPending(false);
    }
  }

  return <Switch checked={value} onCheckedChange={handleChange} disabled={pending} label={label} size="sm" />;
}
